import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { isArray, isPlainObject, snakeCase } from 'lodash';
import { KnownImpressionTypes } from '../components/SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import { ExecutiveSummaryImpressionType } from '../components/SubjectViewer/Modules/ExecutiveSummary/constants';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
  withRetries,
} from '../hooks/useService';
import useService, { ServiceAuth } from '../hooks/useService/useService';
import urls from '../util/urls';

export type AsyncTask<AgentInput = unknown> = {
  id: string;
  status:
    | 'scheduled'
    | 'pending'
    | 'processing'
    | 'failed'
    | 'completed'
    | 'expired';
  created: number;
  scheduled?: number | undefined;
  agentInput: AgentInput;
};

export const isTaskResolved = (task: AsyncTask) =>
  ['failed', 'completed', 'expired'].includes(task.status);

export default class AgentsService {
  static AGENTS = [
    'health-summary-agent',
    'fountainmembersagent',
    'fountaincliniciansagent',
    'action-plan-agent',
    'template-agent',
  ] as const;
  AgentsServiceAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(options?: Partial<AxiosRequestConfig>) {
    this.AgentsServiceAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create(options));
  }

  suggestActionPlan =
    (auth: ServiceAuth) =>
    async ({
      subjectId,
      projectId,
    }: {
      subjectId: string;
      projectId: string;
    }) => {
      return this.AgentsServiceAPI(auth).post(
        urls.api.agents.suggestActionPlan(),
        { subject_id: subjectId, project_id: projectId },
      );
    };

  /**
   * Generate an annual report for a member. If generation is already
   * in progress for the member, a 409 will be returned.
   */
  generateExecutiveSummary =
    (auth: ServiceAuth) => async (args: HealthSummaryInput) => {
      return this.AgentsServiceAPI(auth).post(
        urls.api.agents.generateExecutiveSummary(),
        snakeCaseKeysRecursive(args),
        { validateStatus: status => status < 400 || status === 409 },
      );
    };

  sendFeedback =
    (auth: ServiceAuth) =>
    async ({
      feedback,
      agentName,
      traceId,
    }: {
      feedback: string;
      traceId: string;
      agentName: (typeof AgentsService.AGENTS)[number];
    }) => {
      return this.AgentsServiceAPI(auth).post(urls.api.agents.feedback(), {
        feedback,
        agent_name: agentName,
        trace_id: traceId,
      });
    };

  listInvocations =
    (auth: ServiceAuth) =>
    async ({
      agentName,
      subjectId,
    }: {
      agentName: (typeof AgentsService.AGENTS)[number];
      subjectId: string;
    }) => {
      return this.AgentsServiceAPI(auth).get(
        urls.api.agents.invocations(
          agentName === 'health-summary-agent' ? 'health-summary' : agentName,
          subjectId,
        ),
      );
    };

  retrieveInvocation =
    (auth: ServiceAuth) =>
    async ({
      agentName,
      id,
    }: {
      agentName: (typeof AgentsService.AGENTS)[number];
      id: string;
    }) => {
      return this.AgentsServiceAPI(auth).get<AsyncTask | undefined>(
        urls.api.agents.invocation(
          agentName === 'health-summary-agent' ? 'health-summary' : agentName,
          id,
        ),
      );
    };

  /** Sends an empty `PATCH` request to the invocation to set the task to `pending`.
   * Used when the user wants to bump a scheduled task to immediate execution.
   */
  assumeInvocation =
    (auth: ServiceAuth) =>
    async ({
      agentName,
      id,
    }: {
      agentName: (typeof AgentsService.AGENTS)[number];
      id: string;
    }) => {
      return this.AgentsServiceAPI(auth).patch(
        urls.api.agents.assumeInvocation(
          agentName === 'health-summary-agent' ? 'health-summary' : agentName,
          id,
        ),
      );
    };

  executeTemplate =
    (auth: ServiceAuth) =>
    async ({
      templateId,
      subjectId,
      projectId,
      instructions,
      modelId,
    }: {
      templateId: string;
      subjectId: string;
      projectId: string;
      instructions?: string;
      modelId?: string;
    }) => {
      return this.AgentsServiceAPI(auth).post(
        urls.api.agents.executeTemplate(),
        {
          subject_id: subjectId,
          project_id: projectId,
          template_id: templateId,
          instructions,
          model_id: modelId,
        },
      );
    };
}

export const useSendAgentFeedback = () => {
  const agentsService = new AgentsService();
  const [sendAgentFeedback] = useService(agentsService.sendFeedback, {
    rethrowErrors: true,
  });
  return sendAgentFeedback;
};

export type HealthSummaryInput = HealthSummaryV1Input | HealthSummaryV2Input;

export type HealthSummaryInputBase = {
  subjectId: string;
  projectId: string;
  /** defaults to true */
  mutate?: boolean;
  /** defaults to false */
  skipSummaries?: boolean;
  /** defaults to false */
  skipActionItems?: boolean;
};

export type HealthSummaryV1Input = HealthSummaryInputBase & {
  version: 'v1';
  subjectId: string;
  /** defaults to true */
  skipFindingsSummary?: boolean;
  /**
   * When undefined, regenerate all summaries.
   * When set (e.g., ["brain"]), regenerate only the requested summaries
   */
  requestedSummaries?: ExecutiveSummaryImpressionType[];
};

export type HealthSummaryV2Input = HealthSummaryInputBase & {
  version: 'v2';
  crdId: string;
  /**
   * When undefined, regenerate all summaries.
   * When set (e.g., ["brain"]), regenerate only the requested summaries
   */
  requestedSummaries?: KnownImpressionTypes[];
  instructions?: {
    summaryType: KnownImpressionTypes;
    instructions: string;
  }[];
};

function snakeCaseKeysRecursive(obj: any): any {
  if (isArray(obj)) return obj.map(snakeCaseKeysRecursive);
  if (isPlainObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        snakeCase(key),
        snakeCaseKeysRecursive(value),
      ]),
    );
  }
  return obj;
}
